'use client';

import Link from 'next/link';
import { MapPin, Phone, Mail, Clock, Globe, Building2, X, ArrowRight } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';

import { MenuReviewsSection } from '@/components/reviews/MenuReviewsSection';
import type { PublicMenuBranchDetails } from '@/components/menu/PublicMenuShell';

/**
 * "More about this place" — everything that is ABOUT the place rather than on
 * its menu.
 *
 * WHAT MOVED IN HERE, AND WHY.
 *
 * · Reviews. They used to sit at the very bottom of the menu page, below the
 *   last dish and above the footer. On a 42-item menu essentially nobody
 *   scrolled that far, so the social proof the merchant had collected was
 *   invisible — and for the few who did reach it, it interrupted the one flow
 *   the page exists for (read menu → choose → order) with a question about
 *   whether to trust the place at all. Reviews answer "is this place any good",
 *   which is exactly the question someone opens this sheet to ask. They are also
 *   lazy here for free: the island only mounts, and only fetches, when the sheet
 *   is actually opened.
 *
 * · A link to the branch's own page. `/b/<menu-slug>` has existed the whole
 *   time — the full themed page with the map, every menu, gift cards and
 *   reservations — and the sheet literally titled "About this place" did not
 *   mention it. A sheet is a summary; it should say where the whole thing is.
 *
 * Rendered inside the Place shell, which maps the merchant's palette onto the
 * `--color-*` seam (see lib/customer/place-chrome-style.ts). It therefore reads
 * those tokens — NOT the platform's static `--pub-*` — so a near-black branch
 * theme shows a near-black sheet instead of a cream card clapped over the menu.
 */
export function BranchInfoSheet({
  open,
  onOpenChange,
  details,
  isOpenNow,
  branchId,
  businessName,
  placePageHref,
  switchBranchHref,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  details: PublicMenuBranchDetails;
  isOpenNow?: boolean | null;
  /** Enables the reviews block. Absent on a branch we cannot identify. */
  branchId?: string | null;
  businessName?: string | null;
  /** `/b/<menu-slug>` — the branch's full page. */
  placePageHref?: string | null;
  /**
   * Back to the branch chooser — doc 70 F2.
   *
   * The chooser was a one-way door: every card linked into a branch and nothing
   * linked back, so a guest who landed on the wrong side of the city had to
   * retype the business URL — which returned them to the same branch anyway,
   * because the resolver remembers their last one. Null for a single-branch
   * business, where there is nothing to switch to.
   */
  switchBranchHref?: string | null;
}) {
  const rows: Array<{ icon: typeof MapPin; label: string; value: string }> = [];

  if (details.address) {
    rows.push({ icon: MapPin, label: 'Address', value: details.address });
  }
  if (details.hoursSummary) {
    rows.push({ icon: Clock, label: 'Hours', value: details.hoursSummary });
  }
  if (details.phone) {
    rows.push({ icon: Phone, label: 'Phone', value: details.phone });
  }
  if (details.email) {
    rows.push({ icon: Mail, label: 'Email', value: details.email });
  }
  if (details.locationLabel) {
    rows.push({ icon: Globe, label: 'Location', value: details.locationLabel });
  }
  if (details.timezone) {
    rows.push({ icon: Clock, label: 'Timezone', value: details.timezone });
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="flex max-h-[80svh] w-full !max-w-none flex-col gap-0 overflow-hidden rounded-t-3xl border-x-0 border-b-0 p-0 md:!left-1/2 md:!right-auto md:w-[min(94vw,42rem)] md:-translate-x-1/2"
        style={{
          backgroundColor: 'var(--color-bg-surface)',
          color: 'var(--color-text-primary)',
          borderTop: '1px solid var(--color-border-subtle)',
        }}
      >
        {/* Drag handle */}
        <div className="flex shrink-0 justify-center pt-2.5 pb-1">
          <div className="h-1 w-8 rounded-full bg-black/10" />
        </div>

        {/* Close button */}
        <button
          type="button"
          onClick={() => onOpenChange(false)}
          className="absolute right-3 top-3 z-10 flex h-9 w-9 items-center justify-center rounded-full transition-colors hover:opacity-80"
          style={{ backgroundColor: 'rgba(128,128,128,0.16)', color: 'var(--color-text-secondary)' }}
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>

        {/* Header */}
        <div className="shrink-0 px-5 pt-2 pb-4">
          <div className="flex items-center gap-3">
            <div
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl"
              style={{ backgroundColor: 'var(--color-bg-sunken)', color: 'var(--color-accent)' }}
            >
              <Building2 className="size-5" />
            </div>
            <div className="min-w-0">
              <SheetHeader className="p-0 text-left">
                <SheetTitle className="text-lg font-bold leading-snug" style={{ color: 'var(--color-text-primary)' }}>
                  {details.name || 'About this place'}
                </SheetTitle>
              </SheetHeader>
              {isOpenNow !== null && isOpenNow !== undefined && (
                <span className="mt-0.5 inline-flex items-center gap-1 text-xs font-medium" style={{ color: isOpenNow ? 'var(--color-success)' : 'var(--color-text-secondary)' }}>
                  <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: isOpenNow ? 'var(--color-success)' : 'var(--color-text-secondary)' }} />
                  {isOpenNow ? 'Open now' : 'Currently closed'}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Details and reviews scroll TOGETHER.
            They were two separately-capped blocks, so a branch with six detail
            rows filled the sheet and left reviews a sliver they could not be
            read in. One scroll region lets whichever the guest came for take the
            space, and keeps the branch-page link pinned below it either way. */}
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        {rows.length > 0 && (
          <div className="px-5 pb-5 space-y-0.5">
            {rows.map((row) => {
              const Icon = row.icon;
              return (
                <div
                  key={row.label}
                  className="flex items-start gap-3 rounded-xl px-3 py-2.5"
                  style={{ backgroundColor: 'var(--color-bg-sunken)', border: '1px solid var(--color-border-subtle)' }}
                >
                  <Icon className="mt-0.5 size-4 shrink-0" style={{ color: 'var(--color-accent)' }} />
                  <div className="min-w-0">
                    <p className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: 'var(--color-text-secondary)' }}>
                      {row.label}
                    </p>
                    <p className="mt-0.5 text-sm leading-snug" style={{ color: 'var(--color-text-primary)' }}>
                      {row.value}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {rows.length === 0 && (
          <div className="px-5 pb-4 text-center">
            <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
              No details available for this location.
            </p>
          </div>
        )}

        <div className="px-5 pb-2">
          {branchId ? <MenuReviewsSection branchId={branchId} businessName={businessName} /> : null}
        </div>
        </div>

        {switchBranchHref || placePageHref ? (
          <div
            className="shrink-0 space-y-2 px-5 pb-5 pt-3"
            style={{ borderTop: '1px solid var(--color-border-subtle)' }}
          >
            {/* Listed FIRST and as the plain-bordered option: a guest opening
                this sheet to change location is fixing a wrong turn, and that
                errand should not have to be read past the marketing link. It
                stays visually quieter than the accent button below it because
                it is the rarer of the two. */}
            {switchBranchHref ? (
              <Link
                href={switchBranchHref}
                className="flex w-full items-center justify-between gap-3 rounded-xl border px-4 py-3 text-sm font-semibold transition hover:opacity-90"
                style={{ borderColor: 'var(--color-border)' }}
              >
                <span className="flex items-center gap-2">
                  <MapPin className="size-4 shrink-0" aria-hidden="true" />
                  Order from a different location
                </span>
                <ArrowRight className="size-4 shrink-0 opacity-50" aria-hidden="true" />
              </Link>
            ) : null}
            {placePageHref ? (
            <Link
              href={placePageHref}
              className="flex w-full items-center justify-between gap-3 rounded-xl px-4 py-3 text-sm font-semibold transition-opacity hover:opacity-90"
              style={{
                backgroundColor: 'var(--color-accent)',
                color: 'var(--color-accent-text)',
              }}
            >
              <span>See everything about {details.name || 'this place'}</span>
              <ArrowRight className="size-4 shrink-0" aria-hidden="true" />
            </Link>
            ) : null}
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
